/**
 * Dialog para preview de Template WhatsApp
 * Sprint 2 - Notificame Integration
 * 
 * Exibe visualização do template como aparecerá no WhatsApp
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  Image, 
  ImageIcon,
  Video, 
  VideoIcon,
  FileIcon, 
  ExternalLink,
  Phone,
  MessageSquare,
  CreditCard,
  QrCode,
  Copy
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface WhatsAppTemplate {
  /** URL permanente da mídia do header (Supabase Storage) */
  header_media_url?: string | null;
  id: string;
  inbox_id: string;
  template_id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  rejection_reason: string | null;
  components: any;
  variables: string[] | null;
  example_values: Record<string, string> | null;
  provider_metadata: any;
  created_at: string;
  submitted_at: string | null;
  approved_at: string | null;
  inbox?: {
    name: string;
    identifier: string;
  };
}

interface TemplatePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: WhatsAppTemplate;
}

/**
 * Extrai componente por tipo
 */
const getComponent = (components: any[], type: string) => {
  if (!components || !Array.isArray(components)) return null;
  return components.find(c => c.type === type);
};

/**
 * Substitui variáveis pelos valores de exemplo
 */
const replaceVariables = (text: string, examples: Record<string, string> | null): string => {
  if (!text || !examples) return text;
  
  let result = text;
  Object.entries(examples).forEach(([key, value]) => {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || `{{${key}}}`);
  });
  return result;
};

/**
 * Ícone do formato de header
 */
const HeaderFormatIcon = ({ format }: { format: string }) => {
  switch (format) {
    case 'IMAGE':
      return <Image className="h-4 w-4" />;
    case 'VIDEO':
      return <Video className="h-4 w-4" />;
    case 'DOCUMENT':
      return <FileIcon className="h-4 w-4" />;
    default:
      return null;
  }
};

/**
 * Componente de preview de mídia do header com imagem real ou placeholder
 */
function MediaHeaderPreview({ format, mediaUrl }: { format: string; mediaUrl?: string }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [mediaUrl]);
  
  // Imagem com URL válida
  if (format === 'IMAGE' && mediaUrl && !imageError) {
    return (
      <div className="relative w-full aspect-video bg-slate-100 overflow-hidden">
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <ImageIcon className="h-8 w-8 text-slate-400 animate-pulse" />
          </div>
        )}
        <img
          src={mediaUrl}
          alt="Header"
          className={cn(
            "w-full h-full object-cover transition-opacity",
            imageLoaded ? "opacity-100" : "opacity-0"
          )}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }
  
  // Vídeo com URL — renderiza player real com controles
  if (format === 'VIDEO' && mediaUrl) {
    return (
      <div className="relative w-full aspect-video bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden">
        <video src={mediaUrl} controls preload="metadata" className="w-full h-full object-contain" />
      </div>
    );
  }
  
  // Documento com URL
  if (format === 'DOCUMENT' && mediaUrl) {
    const fileName = mediaUrl.split('/').pop()?.split('?')[0] || 'documento.pdf';
    return (
      <div className="p-4 bg-slate-50 flex items-center gap-3">
        <div className="w-10 h-12 bg-white rounded shadow-sm flex items-center justify-center border">
          <FileText className="h-5 w-5 text-red-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-700 truncate">{decodeURIComponent(fileName)}</p>
          <p className="text-[10px] text-slate-400">Documento</p>
        </div>
      </div>
    );
  }
  
  // Placeholder genérico (sem URL)
  return (
    <div className="bg-slate-100 p-6 flex items-center justify-center">
      <div className="flex flex-col items-center gap-2 text-slate-500">
        <HeaderFormatIcon format={format} />
        <span className="text-xs">{format}</span>
      </div>
    </div>
  );
}


/**
 * Ícone do tipo de botão - Suporta ORDER_DETAILS para PIX
 */
const ButtonTypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'URL':
      return <ExternalLink className="h-3.5 w-3.5" />;
    case 'PHONE_NUMBER':
      return <Phone className="h-3.5 w-3.5" />;
    case 'ORDER_DETAILS':
      return <CreditCard className="h-3.5 w-3.5" />;
    case 'COPY_CODE':
      return <Copy className="h-3.5 w-3.5" />;
    default:
      return <MessageSquare className="h-3.5 w-3.5" />;
  }
};

export function TemplatePreviewDialog({
  open,
  onOpenChange,
  template,
}: TemplatePreviewDialogProps) {
  const components = template.components || [];
  const header = getComponent(components, 'HEADER');
  const body = getComponent(components, 'BODY');
  const footer = getComponent(components, 'FOOTER');
  const buttons = getComponent(components, 'BUTTONS');

  const examples = template.example_values;

  // Configura categoria
  const categoryColors: Record<string, string> = {
    MARKETING: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
    UTILITY: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    AUTHENTICATION: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  };

  // Configura status
  const statusColors: Record<string, string> = {
    APPROVED: 'bg-green-500/10 text-green-600 border-green-500/20',
    PENDING: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    REJECTED: 'bg-destructive/10 text-destructive border-destructive/20',
    PAUSED: 'bg-muted text-muted-foreground border-muted',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* DialogContent com p-0 para controle total do layout */}
      <DialogContent 
        className="max-w-lg p-0 flex flex-col" 
        style={{ maxHeight: '90vh' }}
      >
        {/* Header FIXO - nunca será scrollado */}
        <div className="shrink-0 px-6 pt-6 pb-4 border-b bg-background">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Preview: {template.name}
            </DialogTitle>
          </DialogHeader>

          {/* Motivo da rejeição - no header fixo para visibilidade imediata */}
          {template.status === 'REJECTED' && (
            <div className="mt-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm leading-relaxed">
              <strong>Motivo da rejeição:</strong>{' '}
              {template.rejection_reason || 'Nenhum motivo informado pela Meta.'}
            </div>
          )}
        </div>

        {/* Conteúdo SCROLLÁVEL - usa overflow-y-auto nativo */}
        <div 
          className="flex-1 overflow-y-auto px-6 py-4"
          style={{ maxHeight: 'calc(90vh - 140px)' }}
        >
          <div className="space-y-4">
          {/* Info badges */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={cn("text-xs", categoryColors[template.category])}>
              {template.category}
            </Badge>
            <Badge variant="outline" className={cn("text-xs", statusColors[template.status])}>
              {template.status}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {template.language}
            </Badge>
          </div>

          {/* Preview do WhatsApp */}
          <div className="rounded-xl bg-[#e5ddd5] p-4">
            <div className="max-w-[280px] mx-auto">
              {/* Balão de mensagem - cores fixas para garantir legibilidade em dark mode */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden text-slate-900">
                {/* Header */}
                {header && (
                  <div className="border-b border-slate-200">
                    {header.format === 'TEXT' ? (
                      <div className="px-3 py-2 font-semibold text-sm text-slate-900">
                        {replaceVariables(header.text, examples)}
                      </div>
                    ) : (
                      <MediaHeaderPreview format={header.format} mediaUrl={template.header_media_url || header.example?.header_handle?.[0]} />
                    )}
                  </div>
                )}

                {/* Body */}
                {body && (
                  <div className="px-3 py-2 text-sm whitespace-pre-wrap text-slate-900">
                    {replaceVariables(body.text, examples)}
                  </div>
                )}

                {/* Footer */}
                {footer && (
                  <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-200">
                    {footer.text}
                  </div>
                )}

                {/* Buttons - Com detalhes de URL/telefone/ORDER_DETAILS */}
                {buttons?.buttons && buttons.buttons.length > 0 && (
                  <div className="border-t">
                    {buttons.buttons.map((btn: any, index: number) => (
                      <div key={index} className={cn(index > 0 && "border-t")}>
                        {/* Botão clicável para URLs */}
                        {btn.type === 'URL' && btn.url ? (
                          <a
                            href={btn.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              "w-full px-3 py-2.5 text-sm text-blue-500 font-medium",
                              "flex items-center justify-center gap-2",
                              "hover:bg-muted/50 transition-colors block"
                            )}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            {replaceVariables(btn.text, examples)}
                          </a>
                        ) : btn.type === 'ORDER_DETAILS' ? (
                          /* Botão ORDER_DETAILS (PIX) com estilo verde */
                          <button
                            className={cn(
                              "w-full px-3 py-2.5 text-sm text-green-600 font-medium",
                              "flex items-center justify-center gap-2",
                              "hover:bg-green-50 transition-colors"
                            )}
                          >
                            <CreditCard className="h-3.5 w-3.5" />
                            {replaceVariables(btn.text, examples)}
                          </button>
                        ) : (
                          <button
                            className={cn(
                              "w-full px-3 py-2.5 text-sm text-blue-500 font-medium",
                              "flex items-center justify-center gap-2",
                              "hover:bg-muted/50 transition-colors"
                            )}
                          >
                            <ButtonTypeIcon type={btn.type} />
                            {replaceVariables(btn.text, examples)}
                          </button>
                        )}
                        
                        {/* Info adicional do botão (URL ou telefone) */}
                        {(btn.url || btn.phone_number) && (
                          <div className="px-3 pb-2 text-[10px] text-slate-400 text-center">
                            {btn.url && (
                              <span className="flex items-center justify-center gap-1 truncate">
                                <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                                <span className="truncate">{btn.url}</span>
                              </span>
                            )}
                            {btn.phone_number && (
                              <span className="flex items-center justify-center gap-1">
                                <Phone className="h-2.5 w-2.5 shrink-0" />
                                {btn.phone_number}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Info ORDER_DETAILS (PIX) */}
                        {btn.type === 'ORDER_DETAILS' && (
                          <div className="px-3 pb-2 text-[10px] text-green-600 text-center">
                            <span className="flex items-center justify-center gap-1">
                              <QrCode className="h-2.5 w-2.5 shrink-0" />
                              Pagamento PIX
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Timestamp */}
              <div className="text-right mt-1">
                <span className="text-[10px] text-slate-500">
                  {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          </div>

          {/* Seção Detalhes dos Botões - FORA do balão WhatsApp */}
          {buttons?.buttons && buttons.buttons.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Botões do Template</h4>
              <div className="space-y-1.5">
                {buttons.buttons.map((btn: any, i: number) => {
                  // Verifica se URL é dinâmica (contém {{1}}, {{2}}, etc.)
                  const isDynamicUrl = btn.type === 'URL' && btn.url?.includes('{{');
                  const isOrderDetails = btn.type === 'ORDER_DETAILS';
                  
                  return (
                    <div key={i} className={cn(
                      "flex items-start gap-2 text-xs p-2 rounded",
                      isOrderDetails ? "bg-green-500/10 border border-green-500/20" : "bg-muted/50"
                    )}>
                      <ButtonTypeIcon type={btn.type} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{btn.text}</p>
                        
                        {/* URL do botão */}
                        {btn.type === 'URL' && (
                          <div className="space-y-1">
                            <p className="text-muted-foreground truncate font-mono text-[10px]">
                              {btn.url}
                            </p>
                            {isDynamicUrl && (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge variant="secondary" className="text-[10px] bg-green-500/10 text-green-600 border-green-500/20">
                                  Dinâmica
                                </Badge>
                                {btn.example?.[0] && (
                                  <span className="text-[10px] text-muted-foreground">
                                    Exemplo: <span className="font-mono bg-muted px-1 rounded">{btn.example[0]}</span>
                                  </span>
                                )}
                              </div>
                            )}
                            {!isDynamicUrl && (
                              <Badge variant="outline" className="text-[10px]">
                                Estática
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {/* Telefone do botão */}
                        {btn.type === 'PHONE_NUMBER' && (
                          <p className="text-muted-foreground truncate">
                            {btn.phone_number}
                          </p>
                        )}
                        
                        {/* Quick Reply */}
                        {btn.type === 'QUICK_REPLY' && (
                          <p className="text-muted-foreground">Resposta rápida</p>
                        )}

                        {/* ORDER_DETAILS (PIX) - Seção completa */}
                        {isOrderDetails && (
                          <div className="mt-2 space-y-2">
                            <div className="flex items-center gap-1.5">
                              <QrCode className="h-3 w-3 text-green-600" />
                              <span className="text-green-600 font-medium">Pagamento PIX</span>
                            </div>
                            
                            {/* Detalhes do ORDER_DETAILS - Com dados PIX salvos */}
                            {btn.order_details && (
                              <div className="bg-background/80 rounded p-2 space-y-1.5 text-[10px]">
                                {/* Tipo de pagamento */}
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Tipo:</span>
                                  <span className="font-medium">
                                    {btn.order_details.payment_settings?.[0]?.type === 'pix_dynamic_code' 
                                      ? 'PIX Dinâmico' 
                                      : btn.order_details.payment_settings?.[0]?.type || 'PIX'}
                                  </span>
                                </div>
                                
                                {/* Código PIX Copia-Cola (truncado) */}
                                {btn.order_details.payment_settings?.[0]?.pix_dynamic_code?.code && (
                                  <div className="space-y-1">
                                    <span className="text-muted-foreground">Código PIX:</span>
                                    <div className="bg-muted/50 rounded p-1.5 font-mono text-[9px] break-all max-h-12 overflow-hidden relative">
                                      {btn.order_details.payment_settings[0].pix_dynamic_code.code.length > 80
                                        ? `${btn.order_details.payment_settings[0].pix_dynamic_code.code.substring(0, 80)}...`
                                        : btn.order_details.payment_settings[0].pix_dynamic_code.code}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Merchant Name / Beneficiário */}
                                {btn.order_details.payment_settings?.[0]?.pix_dynamic_code?.merchant_name && (
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Beneficiário:</span>
                                    <span className="font-medium">
                                      {btn.order_details.payment_settings[0].pix_dynamic_code.merchant_name}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Chave PIX */}
                                {btn.order_details.payment_settings?.[0]?.pix_dynamic_code?.key && (
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Chave PIX:</span>
                                    <span className="font-mono text-[9px]">
                                      {btn.order_details.payment_settings[0].pix_dynamic_code.key}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Tipo de chave */}
                                {btn.order_details.payment_settings?.[0]?.pix_dynamic_code?.key_type && (
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Tipo Chave:</span>
                                    <Badge variant="outline" className="text-[9px] h-4">
                                      {btn.order_details.payment_settings[0].pix_dynamic_code.key_type}
                                    </Badge>
                                  </div>
                                )}
                                
                                {/* Valor */}
                                {btn.order_details.total_amount && (
                                  <div className="flex justify-between pt-1 border-t border-green-500/20">
                                    <span className="text-muted-foreground">Valor:</span>
                                    <span className="font-bold text-green-600">
                                      {(btn.order_details.total_amount.value / (btn.order_details.total_amount.offset || 100)).toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: btn.order_details.currency || 'BRL'
                                      })}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Descrição do Item */}
                                {btn.order_details.order?.items?.[0]?.name && (
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Descrição:</span>
                                    <span className="font-medium truncate max-w-[120px]">
                                      {btn.order_details.order.items[0].name}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Referência */}
                                {btn.order_details.reference_id && (
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Referência:</span>
                                    <span className="font-mono text-[9px] truncate max-w-[100px]">
                                      {btn.order_details.reference_id}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Aviso detalhado se não tem order_details definido */}
                            {!btn.order_details && (
                              <div className="bg-background/80 rounded p-2 space-y-2 text-[10px]">
                                <p className="text-muted-foreground">
                                  Este botão de pagamento requer os seguintes campos:
                                </p>
                                <ul className="text-muted-foreground space-y-0.5 list-disc list-inside">
                                  <li>Código PIX Copia-Cola</li>
                                  <li>Nome do Beneficiário</li>
                                  <li>Chave PIX + Tipo</li>
                                  <li>Valor do Pagamento</li>
                                  <li>Descrição do Item</li>
                                </ul>
                                <p className="text-green-600 italic pt-1 border-t border-muted">
                                  💡 Para preencher, edite o template ou configure no momento do envio via chat.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-[10px] shrink-0",
                          isOrderDetails && "bg-green-500/10 text-green-600 border-green-500/30"
                        )}
                      >
                        {btn.type}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Variáveis */}
          {template.variables && template.variables.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Variáveis do Template</h4>
              <div className="flex flex-wrap gap-2">
                {template.variables.map((v, i) => (
                  <Badge key={i} variant="secondary" className="font-mono text-xs">
                    {v}
                    {examples && examples[v.replace(/[{}]/g, '')] && (
                      <span className="ml-1 opacity-60">
                        = {examples[v.replace(/[{}]/g, '')]}
                      </span>
                    )}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Informações adicionais */}
          <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground pt-2 border-t">
            <div>
              <span className="font-medium">ID:</span>
              <p className="font-mono truncate">{template.template_id}</p>
            </div>
            <div>
              <span className="font-medium">Criado em:</span>
              <p>
                {new Date(template.created_at).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
          </div>
        </div>

        {/* Footer FIXO - sempre visível no rodapé */}
        <div className="shrink-0 px-6 py-4 border-t bg-background">
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
